import { AppConfig } from '../../../core/app.config';
import { BACKGROUND_NETWORK_REQ_OPTIONS, BACKGROUND_NETWORK_REQ_UI_OPTIONS } from '../../../core/constants/constants';
import { AutoMappingStatus } from '../../../core/interfaces/auto-mapping-status';
import { NetworkRequestOptions, RequestPaginationData } from '../../../core/interfaces/request';
import { courier, CourierNetworkRequestOptions } from '../../../core/services/courier.service';
import { createHttpParams } from '../../../core/utils/request';
import { EventTypeActionEnum } from '../interfaces/interfaces';

const appConfig = new AppConfig();

const MapperAPI = {
  mapperUrl: appConfig.getMapperURL(),

  getEventType(
    integrationId: any,
    eventTypeName: string,
    abortSignal?: AbortSignal
  ) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/${encodeURIComponent(eventTypeName)}/source-schemas-details`;

    const options: CourierNetworkRequestOptions = {
      uiOptions: {
        showSuccessMsg: false,
        showLoading: false
      },
      signal: abortSignal
    };

    return courier.get(requestUrl, options);
  },

  skipEventType(integrationId: any, eventTypeName: string) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/schema/${encodeURIComponent(eventTypeName)}`;

    const params = {
      skip: true
    };

    return courier.put(requestUrl, {}, params);
  },

  unskipEventType(integrationId: any, eventTypeName: string) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/schema/${encodeURIComponent(eventTypeName)}`;

    const params = {
      skip: false
    };

    return courier.put(requestUrl, {}, params);
  },

  resizeStringFields(integrationId: any, eventTypeName: string) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/fix-varchar-columns/${encodeURIComponent(eventTypeName)}`;

    return courier.post(requestUrl, {});
  },

  resetEventType(integrationId: any, eventTypeName: string) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/schema/${encodeURIComponent(eventTypeName)}`;

    return courier.delete(requestUrl, {});
  },

  getMappingSummary(
    integrationId: number,
    evenTypeName: string,
    abortSignal?: AbortSignal
  ) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/mapping-summary/${encodeURIComponent(evenTypeName)}`;

    const options: CourierNetworkRequestOptions = {
      uiOptions: {
        showLoading: false
      },
      signal: abortSignal
    };

    return courier.get(requestUrl, options);
  },

  updateEventTypeAutoMappingStatus(
    integrationId: any,
    eventTypeName: string,
    status: AutoMappingStatus,
    disableAutoMappingComment?: string
  ) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/schema/${encodeURIComponent(eventTypeName)}/auto-mapping-status`;

    const params: any = {
      status: status,
      disable_auto_mapping_comment: disableAutoMappingComment
    };

    const options: NetworkRequestOptions = {
      uiOptions: {
        showLoading: false
      }
    };

    return courier.put(requestUrl, options, params);
  },

  getSourceSchemaDetails(
    integrationId: any,
    paginationData: RequestPaginationData,
    networkRequestOptions = {}
  ) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/source-schemas-details`;

    if (paginationData.with_warnings) {
      paginationData['status_group'] = 'WITH_WARNINGS';
      delete paginationData.with_warnings;
    }

    const options: NetworkRequestOptions = {
      networkOptions: {
        params: createHttpParams(paginationData)
      },
      uiOptions: {
        showSuccessMsg: false,
        showLoading: false
      },
      ...networkRequestOptions
    };

    return courier.get(requestUrl, options);
  },

  applyBulkAction(integrationId: number, action: EventTypeActionEnum, filter: any, selector: any, force = false) {
    const requestUrl = `${ this.mapperUrl }/${ integrationId }/bulk-event-types-actions`;

    if (filter && filter.status_group) {
      filter.event_type_status_filter = filter.status_group.value;
      delete filter.status_group;
    }

    if (filter && filter.search_event) {
      filter.name = {};
      filter.name = filter.search_event;
      delete filter.search_event;
    }

    const options: NetworkRequestOptions = {
      uiOptions: {
        showSuccessMsg: false
      }
    };

    const params = {
      selector,
      filter,
      action_type: action,
      force
    };

    return courier.post(requestUrl, options, params);
  },

  dropTable(destinationId, tableName) {
    const requestUrl = `${this.mapperUrl}/destination/${destinationId}/schema/${tableName}`;

    const options: NetworkRequestOptions = {
      uiOptions: BACKGROUND_NETWORK_REQ_UI_OPTIONS
    };

    return courier.delete(requestUrl, options);
  },

  updateObjectAutoMappingStatus(pipelineId: number, sourceObjectId: number) {
    const requestURL = `${this.mapperUrl}/${pipelineId}/source-object/${sourceObjectId}/enable-auto-mapping`;

    return courier.put(requestURL, BACKGROUND_NETWORK_REQ_OPTIONS, {});
  },

  getMappingQuery(
    integrationId: number,
    mappingId: number,
    params: any,
    networkRequestOptions = {}
  ) {
    const requestUrl = `${this.mapperUrl}/${integrationId}/schema_mapping/${mappingId}/get-update-schema-query`;

    const options: NetworkRequestOptions = {
      uiOptions: {
        showLoading: false,
        showErrorMsg: false
      },
      ...networkRequestOptions
    };

    return courier.post(requestUrl, options, params);
  },

  getDataTypes(
    destType: string,
    showLoading = true,
    abortSignal?: AbortSignal
  ) {
    const requestUrl = `${this.mapperUrl}/data-types`;

    const options: CourierNetworkRequestOptions = {
      networkOptions: {
        params: createHttpParams({
          destination_type: destType
        })
      },
      uiOptions: {
        showLoading: showLoading
      },
      signal: abortSignal
    };

    return courier.get(requestUrl, options);
  },

  getFileStoragePartitionFormats(abortSignal?: AbortSignal) {
    const requestUrl = `${this.mapperUrl}/destination-partition-formats`;

    const options: CourierNetworkRequestOptions = {
      uiOptions: {
        showLoading: false
      },
      signal: abortSignal
    };

    return courier.get(requestUrl, options);
  }
};

export default MapperAPI;
